import { PropsWithChildren, useEffect, useRef } from 'react';

import {
    type CertExistInfo,
    type CertInfo,
    Channel,
    type CommonRestResponse,
    useAppBridge,
    useChannelContext,
    useUserData,
} from 'scbk-services';
import { useUserContextStore } from 'scbk-services/shared-context/stores/userContextStore';

import { useAutUsrLgnInquirySessionInfo } from '../hooks';
import { AutUsrLgnInquirySessionInfoResponse } from '../types';

type ServerSessionSyncProps = {
    initialized: boolean;
} & PropsWithChildren;

/**
 * 세션정보 동기화 처리 컴포넌트
 * @param param0
 * @returns
 */
const ServerSessionSync: React.FC<ServerSessionSyncProps> = ({ initialized, children }) => {
    const { setUserCommonContext } = useUserContextStore();
    const { clearUserData } = useUserData();
    const { channel } = useChannelContext();
    const { doNGetDeviceStorage, doNGetWebviewStorage } = useAppBridge();

    // 세션동기화 진행중
    const idleHydrateSync = useRef<boolean>(false);

    // 세션 조회 API
    const { mutateAsync: inquirySessionInfoAsync } = useAutUsrLgnInquirySessionInfo({
        skipLoading: true,
        onMutate: (req) => {
            console.warn('ServerSessionSync > inquirySessionInfoAsync(mutate) -  req : ', req);
            idleHydrateSync.current = true;
        },
        onError: (error) => {
            console.warn('ServerSessionSync > inquirySessionInfoAsync(error) - error : ', error);
            clearUserData();
        },
        onSettled: (res) => {
            console.warn('ServerSessionSync > inquirySessionInfoAsync(settled) - res : ', res);
            idleHydrateSync.current = false;
        },
    });

    useEffect(() => {
        if (!channel) {
            return;
        }

        if (!initialized) {
            return;
        }

        // 세션-유저컨텍스트 간 동기화 처리 및 스토리지
        const syncStorage = () => {
            if (idleHydrateSync.current) {
                console.log(
                    'ServerSessionSync > idleHydrateSync - skip : ',
                    idleHydrateSync.current,
                );
                return;
            }

            const keys = [
                'connectType',
                'custName',
                'deviceCertExistYn',
                'finCertExistYn',
                'serial',
                'yoBFlg',
                'notUiCertSeqNum',
                'notUiSimpleKeyToken',
                'notUiCertSignKeyType',
                'smartOtp',
                'finalUseDate',
                'finalUseTime',
                'sessionId',
            ];

            // 세션 조회
            inquirySessionInfoAsync(
                { keys },
                {
                    onSuccess: async (
                        response: CommonRestResponse<AutUsrLgnInquirySessionInfoResponse>,
                    ) => {
                        console.log('ServerSessionSync > syncStorage - response : ', response);
                        const data = response.data.data;

                        if (!data?.sessionInfos) {
                            console.warn(
                                'ServerSessionSync > inquirySessionInfoAsync - not found session',
                            );

                            clearUserData();
                            return;
                        }

                        const sessionData = Object.fromEntries(
                            data.sessionInfos.map((s) => [s.key, s.value]),
                        );

                        let lastLoginType = '';
                        let certInfo: CertInfo | undefined = undefined;
                        if (channel === Channel.MB) {
                            try {
                                lastLoginType = (await doNGetDeviceStorage(
                                    'CMM',
                                    'LAST_LOGIN_TYPE',
                                )) as string;
                            } catch {
                                console.warn('ServerSessionSync fail to get lastLoginType');
                            }

                            let loginCertInfo: CertInfo | undefined = undefined;
                            let certExistInfo: CertExistInfo = {
                                jointCertExist: sessionData?.deviceCertExistYn || '',
                                finCertExist: sessionData?.finCertExistYn || '',
                            } as CertExistInfo;

                            try {
                                const storageCertInfo = await doNGetWebviewStorage('loginCertInfo');

                                if (storageCertInfo) {
                                    loginCertInfo = JSON.parse(storageCertInfo);
                                }
                            } catch {
                                console.warn('ServerSessionSync fail to get certInfo');
                                loginCertInfo = {};
                            }

                            certInfo = { ...loginCertInfo, ...certExistInfo };
                        }

                        setUserCommonContext({
                            connectType: sessionData.connectType,
                            custName: sessionData.custName,
                            lastLoginType: lastLoginType,
                            yoBFlg: sessionData.yoBFlg,
                            certInfo: certInfo,
                            smartOtp: sessionData.smartOtp,
                            finalUseDate: sessionData.finalUseDate,
                            finalUseTime: sessionData.finalUseTime,
                        });
                    },
                },
            );
        };

        // 초기 로딩시 sync 실행
        syncStorage();

        // clean up
        return () => {};
    }, [channel, initialized]);

    // 세션 조회 중 페이지 미노출 처리
    if (idleHydrateSync.current) {
        return null;
    }
    return <>{children}</>;
};

export { ServerSessionSync };
